import React, { useState } from 'react'
import type { GestureResponderEvent } from 'react-native'

import { Icon } from '~/components/icon'
import { Chip } from '../..'

import type { IBadgeProps, ILabelProps, IMultiFilterProps } from '../../types'

import * as S from './index.styles'

export function MultiFilter(props: IMultiFilterProps) {
  const {
    checked = false,
    selected = false,
    size = 'md',
    onPressIn,
    onPressOut,
    children,
    disabled = false,
    readOnly = false,
    visibleLabel = true,
    prefixIcon,
  } = props

  const [isPressed, setIsPressed] = useState(false)

  const isDisabled = disabled || readOnly

  const iconColor = () => {
    if (isDisabled) return 'colorTextNeutralDisabled'
    if (isPressed) return 'colorTextNeutralDefault'
    if (checked) return 'colorTextNeutralDefault'
    return 'colorTextNeutralDefault'
  }

  const iconSize = () => {
    if (size === 'md' && effectiveSelected) return 'extra-small'
    if (size === 'md' && !effectiveSelected) return 'tiny'
    return 'small'
  }

  const defaultProps = {
    checked,
    size,
    type: 'singleFilter',
    disabled: isDisabled,
  }

  const childProps = {
    disabled: isDisabled,
    readOnly: false,
    checked: checked,
    selected: false,
  }

  const { labelComponent, badgeComponent } = React.Children.toArray(
    children
  ).reduce(
    (acc, child) => {
      if (!React.isValidElement(child)) return acc

      if (child.type === Chip.Label) {
        acc.labelComponent = React.cloneElement(
          child,
          childProps as unknown as ILabelProps
        )
      }

      if (child.type === Chip.Badge) {
        childProps.selected = selected
        acc.badgeComponent = React.cloneElement(
          child,
          childProps as unknown as IBadgeProps
        )
      }

      return acc
    },
    {
      labelComponent: null,
      badgeComponent: null,
    } as {
      labelComponent: React.ReactNode
      badgeComponent: React.ReactNode
    }
  )

  const effectiveSelected = badgeComponent ? true : selected

  function handlePressIn(event: GestureResponderEvent) {
    setIsPressed(true)
    onPressIn?.(event)
  }

  function handlePressOut(event: GestureResponderEvent) {
    setIsPressed(false)
    onPressOut?.(event)
  }

  return (
    <S.Root
      {...props}
      {...defaultProps}
      selected={effectiveSelected}
      disabled={isDisabled}
      {...{ isPressed }}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
    >
      <Icon
        size={iconSize()}
        symbol={prefixIcon ?? 'rdicon-options-filter'}
        color={iconColor()}
      />

      {visibleLabel && <S.Content>{labelComponent}</S.Content>}

      {badgeComponent}
    </S.Root>
  )
}
